import React, { useState, useRef, useEffect } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Points, PointMaterial, Float, Stars } from '@react-three/drei';
import * as random from 'maath/random/dist/maath-random.esm';
import { motion } from 'framer-motion';
import { 
  Shield, AlertTriangle, CheckCircle, Atom, 
  Network, Brain, Lock, Database, Zap, Globe 
} from 'lucide-react';

// --- GLOBAL STYLES ---
const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap');
    
    body {
      font-family: 'Poppins', sans-serif;
      background-color: #020617;
      color: white;
      margin: 0;
      overflow-x: hidden; /* Only hide horizontal scroll */
    }
    
    .text-glow {
      text-shadow: 0 0 20px rgba(0, 188, 212, 0.5);
    }
    
    .glass-card {
      background: rgba(2, 6, 23, 0.4);
      backdrop-filter: blur(12px);
      border: 1px solid rgba(255, 255, 255, 0.1);
      box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
    }

    /* Custom Scrollbar */
    ::-webkit-scrollbar {
      width: 8px;
    }
    ::-webkit-scrollbar-track {
      background: #020617; 
    }
    ::-webkit-scrollbar-thumb {
      background: #1e293b; 
      border-radius: 4px;
    }
    ::-webkit-scrollbar-thumb:hover {
      background: #00bcd4; 
    }
  `}</style>
);

// --- 3D BACKGROUND COMPONENTS ---

const StarField = (props) => {
  const ref = useRef();
  const [sphere] = useState(() => random.inSphere(new Float32Array(8000), { radius: 1.5 }));
  
  useFrame((state, delta) => {
    ref.current.rotation.x -= delta / 15;
    ref.current.rotation.y -= delta / 20;
  });

  return (
    <group rotation={[0, 0, Math.PI / 4]}>
      <Points ref={ref} positions={sphere} stride={3} frustumCulled={false} {...props}>
        <PointMaterial transparent color="#00bcd4" size={0.002} sizeAttenuation={true} depthWrite={false} />
      </Points>
    </group>
  );
};

// --- GLASS EARTH COMPONENT ---
// Accepts 'scrollProgress' prop to animate based on scroll
const GlassEarth = ({ scrollProgress }) => {
  const groupRef = useRef();
  const earthRef = useRef();
  const cloudsRef = useRef();

  useFrame((state, delta) => {
    // Continuous rotation
    if (earthRef.current) earthRef.current.rotation.y += delta * 0.1;
    if (cloudsRef.current) cloudsRef.current.rotation.y += delta * 0.15;

    // SCROLL INTERACTION LOGIC
    // Move Earth to the right and scale down as user scrolls
    // scrollProgress is 0 (top) to 1 (bottom)
    
    const targetX = 2 + (scrollProgress.current * 4); // Move right
    const targetScale = 2.5 - (scrollProgress.current * 1.5); // Shrink

    if (groupRef.current) {
        // Smooth lerp for position
        groupRef.current.position.x += (targetX - groupRef.current.position.x) * 0.1;
        groupRef.current.scale.setScalar(
            groupRef.current.scale.x + (targetScale - groupRef.current.scale.x) * 0.1
        );
    }
  });

  return (
    <group ref={groupRef} position={[2, 0, 0]} scale={2.5}>
      <mesh>
        <icosahedronGeometry args={[1, 12]} />
        <meshBasicMaterial color="#00ffff" wireframe transparent opacity={0.15} />
      </mesh>
      <mesh ref={earthRef}>
        <sphereGeometry args={[1.05, 64, 64]} />
        <meshPhysicalMaterial 
            color="#1e1e2e" roughness={0.1} metalness={0.1} transmission={0.6} 
            thickness={2} clearcoat={1} ior={1.5} opacity={0.5} transparent
        />
      </mesh>
      <mesh ref={cloudsRef} scale={1.2}>
         <sphereGeometry args={[1, 64, 64]} />
         <meshStandardMaterial color="#4f46e5" transparent opacity={0.1} wireframe />
      </mesh>
      <mesh rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[1.4, 0.02, 16, 100]} />
        <meshBasicMaterial color="#00bcd4" transparent opacity={0.3} />
      </mesh>
    </group>
  );
};

// --- SCENE MANAGER ---
const Scene = ({ scrollProgress }) => {
    return (
        <>
            <Float speed={2} rotationIntensity={0.5} floatIntensity={0.5}>
                <GlassEarth scrollProgress={scrollProgress} />
            </Float>
            <Stars radius={100} depth={50} count={5000} factor={4} saturation={0} fade speed={1} />
            <StarField />
        </>
    );
};

// --- MAIN PAGE COMPONENT ---
const HomePage = () => {
  // We use a ref to store scroll progress so 3D can access it without re-rendering React
  const scrollProgress = useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = window.scrollY / totalHeight;
      scrollProgress.current = progress; // Update ref (0 to 1)
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="relative w-full min-h-screen bg-[#020617]">
      <GlobalStyles />

      {/* 1. BACKGROUND CANVAS (FIXED) */}
      <div className="fixed inset-0 z-0">
        <Canvas shadows camera={{ position: [0, 0, 5], fov: 45 }}>
          <fog attach="fog" args={['#020617', 5, 15]} />
          <ambientLight intensity={0.5} />
          <pointLight position={[10, 10, 10]} intensity={2} color="#4f46e5" />
          <pointLight position={[-10, -10, -10]} intensity={1} color="#00bcd4" />
          
          <Scene scrollProgress={scrollProgress} />
        </Canvas>
      </div>

      {/* 2. FOREGROUND HTML CONTENT (SCROLLABLE) */}
      <div className="relative z-10 w-full flex flex-col items-center">
        
        {/* HERO SECTION */}
        <section className="h-screen w-full flex flex-col justify-center px-4 sm:px-6 lg:px-20">
          <div className="max-w-3xl mt-20"> 
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 1 }}>
              <div className="inline-block px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-400/30 text-cyan-300 font-semibold mb-6 backdrop-blur-md">
                Next-Gen Quantum Security
              </div>
              <h1 className="text-6xl md:text-8xl font-bold leading-tight mb-6 bg-clip-text text-transparent bg-gradient-to-r from-cyan-300 via-white to-purple-500 text-glow">
                GLOCYBS<br />QUANTUM
              </h1>
              <p className="text-xl text-gray-300 mb-8 font-light max-w-lg leading-relaxed backdrop-blur-sm p-2 rounded">
                Securing the digital future with post-quantum cryptography and AI-driven defense mechanisms.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <a href="#solutions" className="px-8 py-4 bg-white text-black rounded-full font-bold hover:scale-105 transition-transform shadow-[0_0_30px_rgba(255,255,255,0.3)] text-center no-underline">
                  Get Protected
                </a>
                <button className="px-8 py-4 border border-white/20 rounded-full font-bold hover:bg-white/10 transition-colors backdrop-blur-md text-white">
                  View Technology
                </button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* CONTENT SECTION 1: The Crisis */}
        <section className="w-full py-24 px-4 sm:px-6 lg:px-20 max-w-7xl">
          <div className="border-l-4 border-red-500 pl-6 mb-12">
             <h2 className="text-4xl md:text-6xl font-bold text-white mb-2">The Coming Cyber Apocalypse</h2>
             <p className="text-xl text-red-400 font-mono">Why Quantum Security is No Longer Optional</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
              <div className="glass-card p-8 rounded-2xl">
                  <div className="text-red-500 mb-4"><AlertTriangle size={40} /></div>
                  <h3 className="text-2xl font-bold mb-4">1. The Quantum Crisis</h3>
                  <p className="text-gray-300 mb-4 text-sm leading-relaxed">
                      Shor's algorithm allows quantum processors to break in <strong>hours</strong> what takes classical computers <strong>trillions of years</strong>.
                  </p>
                  <div className="bg-red-900/20 p-3 rounded border border-red-500/30 text-xs font-mono">
                      RSA-2048: Broken in &lt; 8 Hours
                  </div>
              </div>
              <div className="glass-card p-8 rounded-2xl border-orange-500/30">
                  <div className="text-orange-500 mb-4"><Database size={40} /></div>
                  <h3 className="text-2xl font-bold mb-4">2. Harvest Now, Decrypt Later</h3>
                  <p className="text-gray-300 mb-4 text-sm leading-relaxed">
                      Attackers are stealing encrypted data <strong>today</strong> to decrypt it <strong>tomorrow</strong>.
                  </p>
                  <div className="bg-orange-900/20 p-3 rounded border border-orange-500/30 text-xs font-mono">
                      Status: Active Threat
                  </div>
              </div>
              <div className="glass-card p-8 rounded-2xl">
                  <div className="text-purple-500 mb-4"><Brain size={40} /></div>
                  <h3 className="text-2xl font-bold mb-4">3. Quantum AI Attacks</h3>
                  <p className="text-gray-300 mb-4 text-sm leading-relaxed">
                      Malware will mutate in real-time. Deepfakes will be perfect. Passwords will be cracked instantly.
                  </p>
              </div>
          </div>
        </section>

        {/* CONTENT SECTION 2: GLOCYBS Approach */}
        <section className="w-full py-24 px-4 sm:px-6 lg:px-20 bg-gradient-to-b from-blue-900/20 to-transparent">
           <div className="max-w-7xl mx-auto text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">The GLOCYBS Approach</h2>
              <p className="text-xl text-cyan-300">Active, predictive, self-evolving cybersecurity.</p>
           </div>
           <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                {icon: <CheckCircle className="text-green-400" size={30} />, title: "Quantum Computing", desc: "Entanglement-based detection & optimization."},
                {icon: <CheckCircle className="text-green-400" size={30} />, title: "Artificial Intelligence", desc: "Adaptive threat recognition & self-learning defense."},
                {icon: <CheckCircle className="text-green-400" size={30} />, title: "Blockchain", desc: "Tamper-proof logging & decentralized identity."},
                {icon: <CheckCircle className="text-green-400" size={30} />, title: "Post-Quantum Crypto", desc: "Lattice-based & hash-based systems."}
              ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4 p-6 glass-card rounded-xl">
                      {item.icon}
                      <div><h4 className="text-xl font-bold text-white">{item.title}</h4><p className="text-gray-400 text-sm mt-2">{item.desc}</p></div>
                  </div>
              ))}
           </div>
        </section>

        {/* CONTENT SECTION 3: BIQUA */}
        <section className="w-full py-24 px-4 sm:px-6 lg:px-20 max-w-7xl">
          <div className="text-center mb-20">
              <h2 className="text-6xl md:text-8xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600 mb-6 pb-2">BIQUA</h2>
              <p className="text-2xl font-light tracking-widest uppercase mb-4">Blockchain • Innovation • Quantum • AI</p>
          </div>

          <div className="space-y-24">
              {/* Pillar 1 */}
              <div className="flex flex-col md:flex-row gap-10 items-center">
                  <div className="w-full md:w-1/2">
                      <div className="text-cyan-400 font-bold mb-2 flex items-center gap-2"><Lock /> PILLAR 01</div>
                      <h3 className="text-4xl font-bold mb-6">Quantum-Resistant Cryptography</h3>
                      <p className="text-gray-300 mb-6 leading-relaxed">The core foundation. We use NIST-recommended algorithms like Lattice-based (Kyber) to ensure your data remains secure.</p>
                      <ul className="grid grid-cols-2 gap-4"><li className="bg-white/5 p-2 rounded text-sm border-l-2 border-cyan-500">Post-quantum key exchange</li><li className="bg-white/5 p-2 rounded text-sm border-l-2 border-cyan-500">Quantum-secure SSL/TLS</li></ul>
                  </div>
                  <div className="w-full md:w-1/2 glass-card h-64 flex items-center justify-center rounded-2xl border border-cyan-500/30">
                     <div className="text-center"><h4 className="text-6xl font-bold text-cyan-500/20">QRC</h4></div>
                  </div>
              </div>
              
              {/* Pillar 2 */}
              <div className="flex flex-col md:flex-row-reverse gap-10 items-center">
                  <div className="w-full md:w-1/2">
                      <div className="text-purple-400 font-bold mb-2 flex items-center gap-2"><Brain /> PILLAR 02</div>
                      <h3 className="text-4xl font-bold mb-6">Quantum Threat Prediction</h3>
                      <p className="text-gray-300 mb-6 leading-relaxed">QTPE scans millions of network events simultaneously—something classical AI cannot achieve.</p>
                      <ul className="grid grid-cols-2 gap-4"><li className="bg-white/5 p-2 rounded text-sm border-l-2 border-purple-500">Grover-optimized Search</li><li className="bg-white/5 p-2 rounded text-sm border-l-2 border-purple-500">Real-time Vector Ranking</li></ul>
                  </div>
                  <div className="w-full md:w-1/2 glass-card h-64 flex items-center justify-center rounded-2xl border border-purple-500/30">
                     <div className="text-center"><h4 className="text-6xl font-bold text-purple-500/20">QTPE</h4></div>
                  </div>
              </div>

              {/* Pillar 3 */}
              <div className="flex flex-col md:flex-row gap-10 items-center">
                  <div className="w-full md:w-1/2">
                      <div className="text-yellow-400 font-bold mb-2 flex items-center gap-2"><Database /> PILLAR 03</div>
                      <h3 className="text-4xl font-bold mb-6">Blockchain-Secured Identity</h3>
                      <p className="text-gray-300 mb-6 leading-relaxed">Immutable logs and decentralized identity ensure zero tampering.</p>
                  </div>
                   <div className="w-full md:w-1/2 glass-card h-64 flex items-center justify-center rounded-2xl border border-yellow-500/30">
                     <div className="text-center"><h4 className="text-6xl font-bold text-yellow-500/20">BLOCK</h4></div>
                  </div>
              </div>

               {/* Pillar 4 */}
               <div className="flex flex-col md:flex-row-reverse gap-10 items-center">
                  <div className="w-full md:w-1/2">
                      <div className="text-green-400 font-bold mb-2 flex items-center gap-2"><Zap /> PILLAR 04</div>
                      <h3 className="text-4xl font-bold mb-6">AI Defense Automation</h3>
                      <p className="text-gray-300 mb-6 leading-relaxed">Self-healing cybersecurity that detects, blocks, and learns from attacks autonomously.</p>
                  </div>
                   <div className="w-full md:w-1/2 glass-card h-64 flex items-center justify-center rounded-2xl border border-green-500/30">
                     <div className="text-center"><h4 className="text-6xl font-bold text-green-500/20">AI</h4></div>
                  </div>
              </div>

              {/* Pillar 5 */}
              <div className="flex flex-col md:flex-row gap-10 items-center">
                  <div className="w-full md:w-1/2">
                      <div className="text-blue-400 font-bold mb-2 flex items-center gap-2"><Network /> PILLAR 05</div>
                      <h3 className="text-4xl font-bold mb-6">Quantum Key Distribution (QKD)</h3>
                      <p className="text-gray-300 mb-6 leading-relaxed">Physics-based security using entanglement. Any eavesdropping attempt is instantly detected.</p>
                  </div>
                   <div className="w-full md:w-1/2 glass-card h-64 flex items-center justify-center rounded-2xl border border-blue-500/30">
                     <div className="text-center"><h4 className="text-6xl font-bold text-blue-500/20">QKD</h4></div>
                  </div>
              </div>

              {/* Pillar 6 & 7 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="glass-card p-8 rounded-2xl border border-pink-500/20">
                      <div className="text-pink-400 font-bold mb-4 flex items-center gap-2"><Globe /> PILLAR 06</div>
                      <h3 className="text-2xl font-bold mb-4">3D Threat Visualization</h3>
                      <p className="text-gray-400">Real-time IP geolocation and attack propagation holograms.</p>
                  </div>
                  <div className="glass-card p-8 rounded-2xl border border-orange-500/20">
                      <div className="text-orange-400 font-bold mb-4 flex items-center gap-2"><Shield /> PILLAR 07</div>
                      <h3 className="text-2xl font-bold mb-4">Zero-Trust Network</h3>
                      <p className="text-gray-400">Every packet verified. Zero trust for devices or users by default.</p>
                  </div>
              </div>
          </div>
        </section>

        {/* CONTENT SECTION 4: Differentiation */}
        <section className="w-full py-24 px-4 sm:px-6 lg:px-20 max-w-7xl">
           <div className="glass-card p-10 rounded-3xl bg-gradient-to-br from-slate-900 to-slate-950">
              <h2 className="text-3xl md:text-5xl font-bold mb-8 text-center">Why GLOCYBS is Decades Ahead</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                  <div>
                      <h3 className="text-gray-500 font-bold mb-4 uppercase tracking-widest text-sm">Traditional Security</h3>
                      <ul className="space-y-4 text-gray-500">
                          <li className="flex gap-2"><AlertTriangle size={18}/> Classical Encryption (Vulnerable)</li>
                          <li className="flex gap-2"><AlertTriangle size={18}/> Manual Monitoring</li>
                      </ul>
                  </div>
                  <div>
                      <h3 className="text-cyan-400 font-bold mb-4 uppercase tracking-widest text-sm">GLOCYBS Quantum Native</h3>
                      <ul className="space-y-4 text-white font-medium">
                          <li className="flex gap-2"><CheckCircle className="text-cyan-400" size={18}/> Quantum Supremacy Algorithms</li>
                          <li className="flex gap-2"><CheckCircle className="text-cyan-400" size={18}/> Holographic 3D Monitoring</li>
                      </ul>
                  </div>
              </div>
           </div>
        </section>

        {/* FOOTER SECTION */}
        <footer className="w-full py-24 px-4 text-center bg-black/50 backdrop-blur-lg">
           <h2 className="text-5xl font-bold mb-6">Step Into the Quantum Future</h2>
           <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
              Secure your organization against tomorrow's threats today with BIQUA.
           </p>
           <button className="px-12 py-5 bg-gradient-to-r from-cyan-600 to-blue-600 rounded-full font-bold text-white hover:scale-105 transition-transform">
              Deploy BIQUA Now
           </button>
           <div className="mt-12 text-gray-600 text-sm">
              © 2025 GLOCYBS. All Rights Reserved.<br/>
              Ghaziabad, Uttar Pradesh, India.
           </div>
        </footer>

      </div>
    </div>
  );
};

export default HomePage;